import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import useFetch, { Status } from '../../../../../hooks/fetch.hook';
import MunicipalityApiModel from '../../../models/municipality.api-model';
import Spinner from '../../../../Shared/Spinner';
import GeneralError from '../../../../Shared/GeneralError';
import SelectFormGroup from '../../../../Shared/SelectFormGroup';
import ValidationInfo from '../../../../../validation/validation-info';
import type { IMunicipality } from '../../../../../models/municipality.model';

function processReceivedParticipatingMunicipalities(data: MunicipalityApiModel[]): IMunicipality[] {
  const result = data?.map((d) => MunicipalityApiModel.mapToBusinessModel(d)) ?? [];
  result.sort((a, b) => a.name.localeCompare(b.name));
  return result;
}

function processReceivedAllMunicipalities(data: MunicipalityApiModel[], participatingMunicipalities: IMunicipality[]): IMunicipality[] {
  if (!participatingMunicipalities) return [];
  // Filter out the participating municipalities
  return data?.filter((x) => !participatingMunicipalities.find((p) => p.gid === x.gid)).map((d) => MunicipalityApiModel.mapToBusinessModel(d)) ?? [];
}

function Municipalities() {
  // === STATE ===
  const [getParticipatingMunicipalitiesResult, setGetParticipatingMunicipalitiesResult] = useFetch<MunicipalityApiModel[]>('/api/paegnaben/admin/municipalities/participating');
  const [getAllMunicipalitiesResult] = useFetch<MunicipalityApiModel[]>('/api/paegnaben/admin/municipalities');
  const [addMunicipalityGid, setAddMunicipalityGid] = useState<string | undefined>(undefined);
  const [addMunicipalityStatus, setAddMunicipalityStatus] = useState<{ status: Status; statusCode: number }>({ status: Status.notperformed, statusCode: 0 });

  // === VARIABLES ===
  const participatingMunicipalities = processReceivedParticipatingMunicipalities(getParticipatingMunicipalitiesResult.data ?? []);
  const availableMunicipalitiesToAdd = processReceivedAllMunicipalities(getAllMunicipalitiesResult.data ?? [], participatingMunicipalities);

  // === EVENT HANDLERS ===
  async function handleAddButtonClick(): Promise<void> {
    if (!addMunicipalityGid) return;
    const antiForgeryTokenElement = document.getElementById('antiForgeryToken') as HTMLInputElement;
    const response = await fetch(`/api/paegnaben/admin/municipalities/participating`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': antiForgeryTokenElement.value },
      body: JSON.stringify({ municipalityGid: addMunicipalityGid }),
    });
    if (response.ok) {
      setAddMunicipalityStatus({ status: Status.success, statusCode: response.status });
      setGetParticipatingMunicipalitiesResult((ps) => ({
        ...ps,
        data: [
          ...(ps?.data ?? []),
          ...(getAllMunicipalitiesResult.data?.find((m) => m.gid === addMunicipalityGid) ? [getAllMunicipalitiesResult.data.find((m) => m.gid === addMunicipalityGid)!] : []),
        ],
      }));
      setAddMunicipalityGid(undefined);
    } else {
      setAddMunicipalityStatus({ status: Status.failed, statusCode: response.status });
    }
  }

  // === RENDER ===
  if (
    (getParticipatingMunicipalitiesResult.status === Status.failed && getParticipatingMunicipalitiesResult.httpResponseStatusCode === 401) ||
    (addMunicipalityStatus.status === Status.failed && addMunicipalityStatus.statusCode === 401)
  ) {
    return <Navigate to="/session-utgangen" />;
  }
  if (
    (getParticipatingMunicipalitiesResult.status === Status.failed && getParticipatingMunicipalitiesResult.httpResponseStatusCode === 403) ||
    (addMunicipalityStatus.status === Status.failed && addMunicipalityStatus.statusCode === 403)
  ) {
    return <Navigate to="/atkomst-saknas" />;
  }

  return (
    <>
      <h1>Kommuner</h1>
      {(getParticipatingMunicipalitiesResult.status === Status.loading || getAllMunicipalitiesResult.status === Status.loading) && <Spinner className="my-4 my-md-5" />}
      {(getParticipatingMunicipalitiesResult.status === Status.failed || getAllMunicipalitiesResult.status === Status.failed) && (
        <GeneralError errorText="Kunde inte hämta kommuner" className="mb-4" />
      )}
      {getParticipatingMunicipalitiesResult.status === Status.success && getAllMunicipalitiesResult.status === Status.success && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Namn</th>
                <th scope="col">
                  <span className="visually-hidden">Åtgärder</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {participatingMunicipalities?.map((municipality) => (
                <tr key={municipality.gid}>
                  <th scope="row" className="w-100">
                    {municipality.name}
                  </th>
                  <td>
                    <Link to={`/kommuner/${municipality.gid}`}>Hantera</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <form id="form">
            <SelectFormGroup
              id="add-municipality"
              labelText="Kommun"
              placeholder="Välj kommun att lägga till"
              options={availableMunicipalitiesToAdd.map((m) => ({ value: m.gid, text: m.name }))}
              required={true}
              state={{ value: addMunicipalityGid, validationInfo: new ValidationInfo() }}
              onChange={setAddMunicipalityGid}
            />
            <button type="button" className="btn btn-primary" onClick={handleAddButtonClick}>
              Lägg till kommun
            </button>
            {addMunicipalityStatus.status === Status.failed && <GeneralError errorText="Kunde inte lägga till kommun" className="mt-2" />}
          </form>
        </>
      )}
    </>
  );
}

export default Municipalities;
